import { useRef, useEffect } from 'react';

type ParsedContentLike = { type: string; content: string | unknown };

type MessageLike = {
  id: string;
  type?: string;
  isStreaming?: boolean;
  content?: string;
  parsedContent?: ParsedContentLike[];
};

export const useAutoScroll = <T extends MessageLike[]>(messages: T) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevLastMessageRef = useRef<string | null>(null);
  const prevContentLengthRef = useRef<number>(0);
  const prevParsedContentLengthRef = useRef<number>(0);

  useEffect(() => {
    if (!messages.length) {
      prevLastMessageRef.current = null;
      prevContentLengthRef.current = 0;
      prevParsedContentLengthRef.current = 0;
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const lastMessageKey = `${lastMessage.id}-${lastMessage.type}`;
    const contentLength = lastMessage.content?.length || 0;

    // Also track parsed content changes for streaming responses
    // Calculate total text length from parsedContent for more accurate streaming detection
    // Handle both string content and other types (like ChartSpec)
    const parsedContentLength =
      lastMessage.parsedContent?.reduce((total, block) => {
        const content = block.content;
        if (typeof content === 'string') {
          return total + content.length;
        }
        return total;
      }, 0) || 0;

    // Scroll when:
    // 1. Last message changes (new message or type change)
    // 2. Streaming content grows (either raw content or parsed content)
    const isNewMessage = lastMessageKey !== prevLastMessageRef.current;
    const isStreamingGrowth =
      lastMessage.isStreaming &&
      (contentLength > prevContentLengthRef.current ||
        parsedContentLength > prevParsedContentLengthRef.current);

    if (isNewMessage || isStreamingGrowth) {
      prevLastMessageRef.current = lastMessageKey;
      prevContentLengthRef.current = contentLength;
      prevParsedContentLengthRef.current = parsedContentLength;

      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          // Use 'auto' during streaming for smoother rapid updates
          behavior: lastMessage.isStreaming ? 'auto' : 'smooth',
        });
      }
    }
  }, [messages]);

  return containerRef;
};
